import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./DashboardHeader.css";
import axios from "axios";
import dashboardLogo from "../../imgs/logo 2.webp";
import { useDashboardNotifications } from "../../contexts/DashboardNotificationsContext";
import {
  isStoreProductsOnlyAdmin,
  clearAdminDashboardRole,
} from "../../utils/adminAccess";

function formatNotificationTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
}

function getTimeGroup(dateStr) {
  const hoursAgo = (Date.now() - new Date(dateStr)) / 3600000;
  if (hoursAgo < 24) return "Today";
  if (hoursAgo < 168) return "This Week";
  return "Earlier";
}

function groupNotifications(items) {
  const order = ["Today", "This Week", "Earlier"];
  const map = { Today: [], "This Week": [], Earlier: [] };
  items.forEach((n) => map[getTimeGroup(n.createdAt)].push(n));
  return order.filter((g) => map[g].length > 0).map((g) => ({ label: g, items: map[g] }));
}

function NotifAvatar({ notification }) {
  const initial = notification.senderName
    ? notification.senderName.charAt(0).toUpperCase()
    : notification.type === "order"
    ? "O"
    : "M";
  return (
    <div className={`notif-avatar notif-avatar--${notification.type}`}>
      {initial}
    </div>
  );
}

function DashboardHeader({ toggleSidebar }) {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { notifications, unreadCount, markAsRead, markAllAsRead } =
    useDashboardNotifications();
  const hideNotifications = isStoreProductsOnlyAdmin();
  const token =
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

  useEffect(() => {
    if (!token || token.trim() === "") return;
    const fetchAdminData = async () => {
      try {
        await axios.get("/admin/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        if (error?.response?.status === 401) {
          localStorage.removeItem("authToken");
          sessionStorage.removeItem("authToken");
          clearAdminDashboardRole();
          navigate("/dashboard/login", { replace: true });
        }
      }
    };
    fetchAdminData();
  }, [token, navigate]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const groups = groupNotifications(notifications.slice(0, 8));

  return (
    <header className="dashboard-header">
      <div className="container">
        <button className="side-bar-toggle-btn" onClick={toggleSidebar}>
          <i className="uil uil-apps"></i>
        </button>
        <div className="search" style={{ display: "none" }}>
          <input type="text" placeholder="Search.." />
          <i className="uil uil-search"></i>
        </div>
        <Link to="/" className="dashboard-header-logo" aria-label="Alramlaa - Home">
          <img src={dashboardLogo} alt="Alramlaa" />
        </Link>
        <div className="dashboard-header-actions">
          {!hideNotifications ? (
            <div className="dashboard-notifications-wrap" ref={dropdownRef}>
              <button
                type="button"
                className="dashboard-notifications-btn"
                onClick={() => setIsDropdownOpen((o) => !o)}
                aria-label="Notifications"
                aria-expanded={isDropdownOpen}
              >
                <i className="uil uil-bell"></i>
                {unreadCount > 0 && (
                  <span className="dashboard-notifications-badge">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>

              {isDropdownOpen && (
                <div className="dashboard-notifications-dropdown">
                  <div className="dashboard-notifications-dropdown-header">
                    <h4>
                      Notifications
                      {unreadCount > 0 && (
                        <span className="notif-header-count">{unreadCount}</span>
                      )}
                    </h4>
                    <div className="notif-header-actions">
                      {unreadCount > 0 && (
                        <button
                          type="button"
                          className="notif-mark-all-btn"
                          onClick={markAllAsRead}
                        >
                          Mark all read
                        </button>
                      )}
                      <Link
                        to="/dashboard/notifications"
                        onClick={() => setIsDropdownOpen(false)}
                        className="dashboard-notifications-view-all"
                      >
                        View all
                      </Link>
                    </div>
                  </div>

                  <div className="dashboard-notifications-dropdown-list">
                    {notifications.length === 0 ? (
                      <div className="dashboard-notifications-empty">
                        <i className="uil uil-bell-slash notif-empty-icon" />
                        <p>You're all caught up!</p>
                      </div>
                    ) : (
                      groups.map(({ label, items }) => (
                        <div key={label} className="notif-group">
                          <div className="notif-group-label">{label}</div>
                          {items.map((n) => (
                            <Link
                              key={n.id}
                              to={n.link}
                              className={`dashboard-notifications-item${n.read ? " is-read" : ""}`}
                              onClick={() => {
                                markAsRead(n.id);
                                setIsDropdownOpen(false);
                              }}
                            >
                              <NotifAvatar notification={n} />
                              <div className="notif-item-body">
                                <div className="notif-item-row">
                                  <span className="notif-item-sender">
                                    {n.senderName}
                                  </span>
                                  <span className="notif-item-time">
                                    {formatNotificationTime(n.createdAt)}
                                  </span>
                                </div>
                                <span className="notif-item-preview">
                                  {n.preview}
                                </span>
                                <span className="notif-item-type-tag">
                                  <i
                                    className={`uil ${
                                      n.type === "order"
                                        ? "uil-shopping-bag"
                                        : "uil-envelope"
                                    }`}
                                  />
                                  {n.title}
                                </span>
                              </div>
                              {!n.read && <span className="notif-unread-dot" />}
                            </Link>
                          ))}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}

export default DashboardHeader;
